"use client";

import { useState, type ComponentProps, type Key } from "react";
import {
	Button,
	Icon,
	MenuDropdown,
	RadioGroup,
	Sheets,
	Text,
	type RadioGroupProps
} from "@pamperedchef/design-system";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";

import type { SortControlPresentation, SortOption } from "../types";

import * as styles from "./SortControl.module.scss";

type MenuItems = ComponentProps<typeof MenuDropdown>["items"];
type RadioGroupChange = RadioGroupProps["onChange"];

export interface SortControlLabels {
	sheetTitle: string;
	apply: string;
	cancel: string;
	close: string;
}

const DEFAULT_LABELS: SortControlLabels = {
	sheetTitle: "Sort by",
	apply: "Apply",
	cancel: "Cancel",
	close: "Close sort"
};

export interface SortControlProps {
	options: SortOption[];
	selected: string;
	onChange: (value: string) => void;
	label?: string;
	/** `menu` applies on choosing; `sheet` stages a choice until Apply. */
	presentation?: SortControlPresentation;
	labels?: Partial<SortControlLabels>;
	className?: string;
}

export function SortControl({
	options,
	selected,
	onChange,
	label = "Sort",
	presentation = "menu",
	labels,
	className
}: SortControlProps): React.JSX.Element | null {
	if (options.length === 0) return null;
	if (presentation === "sheet" && options.length > 1) {
		return (
			<SortSheet
				options={options}
				selected={selected}
				onChange={onChange}
				label={label}
				labels={{ ...DEFAULT_LABELS, ...labels }}
				className={className}
			/>
		);
	}

	const selectedIndex = Math.max(
		0,
		options.findIndex((option) => option.value === selected)
	);

	const current = `${label}: ${options[selectedIndex]?.label}`;

	function handleAction(key: Key): void {
		const option = options[Number(key)];
		if (option && option.value !== selected) onChange(option.value);
	}

	return (
		<div
			{...classNameHelper(styles.sort, className)}
			data-component="SortControl"
		>
			{options.length < 2 ? (
				<Text tag="p" className={styles.single}>
					{current}
				</Text>
			) : (
				<MenuDropdown
					items={
						options.map((option) => ({
							label: option.label
						})) as unknown as MenuItems
					}
					onAction={handleAction}
					selectionMode="single"
					selectedKeys={[String(selectedIndex)]}
					placement="bottom end"
				>
					<Icon name="filter-lines" aria-hidden />
					{current}
				</MenuDropdown>
			)}
		</div>
	);
}

interface SortSheetProps extends Omit<SortControlProps, "labels"> {
	label: string;
	labels: SortControlLabels;
}

function SortSheet({
	options,
	selected,
	onChange,
	label,
	labels,
	className
}: SortSheetProps): React.JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const [staged, setStaged] = useState(selected);
	const current =
		options.find((option) => option.value === selected) ?? options[0];
	const fullLabel = `${label}: ${current.label}`;
	const shortLabel = current.shortLabel && `${label}: ${current.shortLabel}`;

	function setOpen(open: boolean): void {
		if (open) setStaged(selected);
		setIsOpen(open);
	}

	function apply(): void {
		if (staged !== selected) onChange(staged);
		setIsOpen(false);
	}

	return (
		<div
			{...classNameHelper(styles.sort, className)}
			data-component="SortControl"
			data-presentation="sheet"
		>
			<Sheets
				isOpen={isOpen}
				onOpenChange={setOpen}
				title={labels.sheetTitle}
				closeButtonLabel={labels.close}
				triggerElement={
					<Button
						kind="tertiary"
						size="sm"
						leadingIcon={{ name: "filter-lines" }}
						aria-label={shortLabel ? fullLabel : undefined}
					>
						{shortLabel ? (
							<>
								<span className={styles.full}>{fullLabel}</span>
								<span className={styles.short}>
									{shortLabel}
								</span>
							</>
						) : (
							fullLabel
						)}
					</Button>
				}
				primaryButton={<Button onPress={apply}>{labels.apply}</Button>}
				secondaryButton={
					<Button onPress={() => setIsOpen(false)}>
						{labels.cancel}
					</Button>
				}
			>
				<RadioGroup
					aria-label={labels.sheetTitle}
					className={styles.options}
					value={staged}
					onChange={
						((value: string) =>
							setStaged(value)) as RadioGroupChange
					}
					items={options.map((option) => ({
						value: option.value,
						children: option.label,
						"aria-label": option.label
					}))}
				/>
			</Sheets>
		</div>
	);
}
